import React, { HTMLAttributes } from 'react';

import PropTypes from 'prop-types';

import { useDeprecate } from '@adsk/alloy-react-helpers';

import Tab from './Tab';
import Tabs from './Tabs';

export type TabAdapterTab = {
  name: () => string;
  id: string;
  url?: string | (() => string);
  onClick?: () => void;
};

type TabAdapterProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange' | 'style'
> & {
  tabs: TabAdapterTab[];
  onTabChange?: (tab: TabAdapterTab) => void;
  canClickSelectedTab?: boolean;
  selectedTab?: TabAdapterTab;
};

const TabsAdapter = ({
  className,
  tabs,
  canClickSelectedTab,
  selectedTab,
  onTabChange,
  ...props
}: TabAdapterProps) => {
  useDeprecate(true, '@adsk/alloy-react-tabs', 'TabsAdapter', 'Tabs');

  const onChange = (tab: string) => {
    const matchingTab = tabs.find((t) => t.id === tab);

    if (matchingTab) {
      onTabChange?.(matchingTab);
    }
  };

  return (
    <Tabs
      className={className}
      active={selectedTab?.id}
      onChange={onChange}
      canClickSelectedTab={canClickSelectedTab}
      {...props}
    >
      {tabs.map((tab) => (
        <Tab key={tab.id} tab={tab.id} label={tab.name()} />
      ))}
    </Tabs>
  );
};

export const tabShape = PropTypes.shape({
  /**
   * A function that returns the name of the tab
   */
  name: PropTypes.func.isRequired,
  /**
   * The id of the tab (used to match with the selectedTabId)
   */
  id: PropTypes.string.isRequired,
  /**
   * Url to open on mouse middle-click / right-click => open in new tab/window
   * If no onClick is passed for this tab, then also the url to open in a new tab on left click
   */
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Optional function to run when the tab is pressed
   */
  onClick: PropTypes.func,
});

TabsAdapter.displayName = 'TabsAdapter';

TabsAdapter.propTypes = {
  /**
   * An optional function that will be called whenever a tab is clicked in the tab navigation area
   */
  onTabChange: PropTypes.func,
  /**
   * The list of tabs in the tab navigation area
   */
  tabs: PropTypes.arrayOf(tabShape).isRequired,
  /**
   * Whether selected tabs are clickable or not. Defaults to true
   */
  canClickSelectedTab: PropTypes.bool,
  /**
   * A custom className for the component container
   */
  className: PropTypes.string,
  /**
   * The selected tab object to highlight in the tab navigation area
   */
  selectedTab: tabShape,
};

export default TabsAdapter;
